/**
 * Production API Implementation of ISocietyRepository
 */
import api from '../../../lib/api';
import { logger } from '../../../core/logger';
import { Society, SocietyType } from '../types/society';
import { Address } from '../../shared/types';
import { dataUtils } from '../../../lib/dataUtils';
import { ISocietyRepository } from './ISocietyRepository';

export class ApiSocietyRepository implements ISocietyRepository {
  async getAll(signal?: AbortSignal): Promise<Society[]> {
    try {
      const response = await api.get('/societies?size=100', { signal });
      const result = dataUtils.normalizeLookup<Society>(response.data, 'societyId', 'societyName');
      logger.info('Fetched societies', { count: result.length });
      return result;
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') throw error;
      logger.error('Failed to fetch societies', error?.response?.status ?? error?.message);
      throw error;
    }
  }

  async getTypes(signal?: AbortSignal): Promise<SocietyType[]> {
    try {
      const response = await api.get('/societies/types', { signal });
      return dataUtils.normalizeLookup<SocietyType>(response.data, 'societyTypeId', 'societyTypeName');
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') throw error;
      logger.error('Failed to fetch society types', error?.message);
      throw error;
    }
  }

  async previewCode(name: string): Promise<string> {
    try {
      const response = await api.get(`/societies/preview-code?name=${encodeURIComponent(name || '')}`);
      const d = response.data;
      if (typeof d?.data === 'string') return d.data;
      if (typeof d?.message === 'string' && d.message.includes('-')) return d.message;
      if (typeof d === 'string') return d;
      return '';
    } catch (error) {
      logger.error('Failed to preview society code', error);
      return '';
    }
  }

  async create(society: any, signal?: AbortSignal): Promise<Society> {
    const response = await api.post('/societies', society, { signal });
    return dataUtils.normalizeItem<Society>(response.data, 'societyId', 'societyName');
  }

  async getById(id: number, signal?: AbortSignal): Promise<Society> {
    const response = await api.get(`/societies/${id}`, { signal });
    return dataUtils.normalizeItem<Society>(response.data, 'societyId', 'societyName');
  }

  async update(id: number, society: any, signal?: AbortSignal): Promise<Society> {
    const response = await api.put(`/societies/${id}`, society, { signal });
    return dataUtils.normalizeItem<Society>(response.data, 'societyId', 'societyName');
  }

  async delete(id: number, signal?: AbortSignal): Promise<void> {
    await api.delete(`/societies/${id}`, { signal });
  }

  async addAddress(societyId: number, address: Address, signal?: AbortSignal): Promise<Address> {
    const response = await api.post(`/societies/${societyId}/addresses`, address, { signal });
    return response.data;
  }

  async updateAddress(societyId: number, targetId: number, address: Partial<Address>, signal?: AbortSignal): Promise<Address> {
    const response = await api.put(`/societies/${societyId}/addresses/${targetId}`, address, { signal });
    return response.data;
  }

  async deleteAddress(societyId: number, targetId: number, signal?: AbortSignal): Promise<void> {
    await api.delete(`/societies/${societyId}/addresses/${targetId}`, { signal });
  }

  async setPrimaryAddress(societyId: number, targetId: number, signal?: AbortSignal): Promise<Address> {
    const response = await api.patch(`/societies/${societyId}/addresses/${targetId}/primary`, {}, { signal });
    return response.data;
  }
}
